import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Users, MessageSquare, LogOut, ArrowLeft, Mail, Phone, Wallet, MapPin, ChevronRight, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LEAD_STATUSES } from "@/data/urban";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => { try { return new Date(v).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return String(v); } };

const StatCard = ({ icon: Ic, label, value }) => (
  <div className="rounded-2xl border border-hairline bg-white p-5">
    <div className="flex items-center gap-3">
      <span className="h-10 w-10 rounded-md bg-black text-white flex items-center justify-center"><Ic className="h-5 w-5" /></span>
      <div><div className="overline text-ink-soft">{label}</div><div className="font-heading text-2xl font-extrabold text-ink">{value}</div></div>
    </div>
  </div>
);

const LeadRow = ({ l, onSave }) => {
  const [status, setStatus] = useState(l.status || "New");
  const [notes, setNotes] = useState(l.notes || "");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await axios.patch(`${API}/admin/leads/${l.id}`, { status, notes }, { withCredentials: true }); onSave({ ...l, status, notes }); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-hairline bg-white overflow-hidden">
      <div className="p-5 grid grid-cols-12 gap-3 items-center">
        <div className="col-span-12 sm:col-span-3">
          <div className="font-heading font-bold text-ink">{l.name}</div>
          <div className="text-xs text-ink-soft">{fmt(l.created_at)}</div>
        </div>
        <div className="col-span-12 sm:col-span-3 text-sm text-ink space-y-0.5">
          <a href={`tel:${l.mobile}`} className="flex items-center gap-1.5"><Phone className="h-3 w-3 text-ink-soft" />{l.mobile}</a>
          {l.email && <a href={`mailto:${l.email}`} className="flex items-center gap-1.5 truncate"><Mail className="h-3 w-3 text-ink-soft" />{l.email}</a>}
          {l.city && <div className="flex items-center gap-1.5 text-ink-soft"><MapPin className="h-3 w-3" />{l.city}</div>}
        </div>
        <div className="col-span-12 sm:col-span-3 text-xs text-ink-soft space-y-0.5">
          {l.service && <div><span className="text-ink-soft">Service:</span> <span className="text-ink font-semibold">{l.service}</span></div>}
          {l.budget && <div className="flex items-center gap-1.5"><Wallet className="h-3 w-3" /> {l.budget}</div>}
          {l.source && <div>Source: <span className="text-ink font-semibold">{l.source}</span></div>}
          {l.product_name && <div>Product: <span className="text-ink font-semibold">{l.product_name}</span></div>}
        </div>
        <div className="col-span-12 sm:col-span-3 flex items-center gap-2">
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid={`lead-status-${l.id}`} className="h-9 rounded-md border border-hairline bg-white px-2 text-xs text-ink flex-1">
            {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button type="button" onClick={() => setExpanded((v) => !v)} className="text-xs text-ink hover:opacity-70">{expanded ? "Hide" : "Notes"}</button>
        </div>
      </div>
      {expanded && (
        <div className="px-5 pb-5 border-t border-hairline pt-4">
          {l.message && <p className="text-sm text-ink-soft mb-3 whitespace-pre-line">{l.message}</p>}
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes..." rows={3} className="w-full rounded-md border border-hairline bg-white px-3 py-2 text-sm text-ink focus:outline-none focus:border-ink" />
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" data-testid={`lead-save-${l.id}`} onClick={save} disabled={busy} className="bg-black text-white px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider hover:bg-ink disabled:opacity-60">{busy ? "Saving..." : "Save"}</button>
          </div>
        </div>
      )}
    </div>
  );
};

const ChatSession = ({ s, open, onToggle }) => (
  <div className="rounded-2xl border border-hairline bg-white overflow-hidden">
    <button type="button" onClick={onToggle} className="w-full flex items-center justify-between p-4 hover:bg-canvas transition-colors">
      <div className="text-left">
        <div className="font-medium text-ink">Session · <span className="font-mono text-xs text-ink-soft">{s.session_id.slice(0, 8)}</span></div>
        <div className="text-xs text-ink-soft mt-0.5">{fmt(s.started_at)} · {s.count} messages</div>
      </div>
      <ChevronRight className={`h-4 w-4 text-ink-soft transition-transform ${open ? "rotate-90" : ""}`} />
    </button>
    {open && (
      <div className="px-4 pb-4 space-y-2 max-h-80 overflow-y-auto bg-canvas">
        {s.messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${m.role === "user" ? "bg-black text-white" : "bg-white border border-hairline text-ink"}`}>{m.content}</div>
          </div>
        ))}
      </div>
    )}
  </div>
);

export const Admin = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [chats, setChats] = useState([]);
  const [stats, setStats] = useState({ leads: 0, chat_sessions: 0 });
  const [openChat, setOpenChat] = useState(null);
  const [busy, setBusy] = useState(true);
  const [tab, setTab] = useState("leads");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login", { replace: true, state: { from: "/admin" } }); return; }
    (async () => {
      setBusy(true);
      try {
        const [l, c, s] = await Promise.all([
          axios.get(`${API}/admin/leads`, { withCredentials: true }),
          axios.get(`${API}/admin/chats`, { withCredentials: true }),
          axios.get(`${API}/admin/stats`, { withCredentials: true }),
        ]);
        setLeads(l.data || []); setChats(c.data || []); setStats(s.data || {});
      } finally { setBusy(false); }
    })();
  }, [user, loading, navigate]);

  const filtered = useMemo(() => leads.filter((l) => {
    if (statusFilter !== "All" && (l.status || "New") !== statusFilter) return false;
    if (!q) return true;
    const hay = `${l.name} ${l.mobile} ${l.email || ""} ${l.city || ""} ${l.service || ""} ${l.product_name || ""}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  }), [leads, q, statusFilter]);

  const doLogout = async () => { await logout(); navigate("/login", { replace: true }); };
  const onSave = (updated) => setLeads((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));

  if (loading || !user) return <div className="min-h-screen bg-canvas flex items-center justify-center text-ink-soft">Loading admin…</div>;

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-hairline">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-back-home" className="inline-flex items-center gap-1.5 text-ink-soft hover:text-ink text-sm"><ArrowLeft className="h-4 w-4" /> Back to site</Link>
            <span className="hidden sm:inline font-heading font-bold text-ink ml-4">Admin Console</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2">
              <div className="text-right leading-tight"><div className="text-sm font-semibold text-ink">{user.name}</div><div className="text-[10px] text-ink-soft">{user.email}</div></div>
            </div>
            <button type="button" data-testid="admin-logout" onClick={doLogout} className="inline-flex items-center gap-1.5 rounded-md border border-hairline px-4 py-2 text-sm text-ink hover:border-ink"><LogOut className="h-4 w-4" /> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <div className="overline text-ink-soft">Dashboard</div>
        <h1 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold">Welcome back, {user.name.split(" ")[0]}.</h1>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard icon={Users} label="Total Leads" value={stats.leads || 0} />
          <StatCard icon={MessageSquare} label="Chat Sessions" value={stats.chat_sessions || 0} />
          <StatCard icon={Users} label="New (unhandled)" value={leads.filter((l) => (l.status || "New") === "New").length} />
        </div>

        <div className="mt-8 flex items-center gap-2 border-b border-hairline">
          {["leads", "chats"].map((k) => (
            <button key={k} type="button" data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
              className={`px-5 py-3 font-medium text-sm border-b-2 -mb-px transition-colors ${tab === k ? "border-black text-ink" : "border-transparent text-ink-soft hover:text-ink"}`}>
              {k === "leads" ? `Leads (${leads.length})` : `Chats (${chats.length})`}
            </button>
          ))}
        </div>

        {tab === "leads" && (
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads..." className="w-full h-11 pl-10 pr-4 rounded-md border border-hairline bg-white text-ink text-sm focus:outline-none focus:border-ink" /></div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-11 rounded-md border border-hairline bg-white px-4 text-sm text-ink"><option value="All">All statuses</option>{LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
          </div>
        )}

        <div className="mt-6">
          {busy ? <div className="text-ink-soft py-10 text-center">Loading…</div> :
            tab === "leads" ? (
              filtered.length === 0 ? <div className="text-ink-soft py-10 text-center">No leads match.</div> :
              <div className="space-y-3" data-testid="admin-leads">{filtered.map((l) => <LeadRow key={l.id} l={l} onSave={onSave} />)}</div>
            ) : chats.length === 0 ? <div className="text-ink-soft py-10 text-center">No chats yet.</div> :
              <div className="space-y-3" data-testid="admin-chats">{chats.map((s) => <ChatSession key={s.session_id} s={s} open={openChat === s.session_id} onToggle={() => setOpenChat((k) => (k === s.session_id ? null : s.session_id))} />)}</div>
          }
        </div>
      </main>
    </div>
  );
};
